<i18n>
{
	"en": {
		"locationsFound": "{count} found",
		"save": "Save",
		"cancelLabel": "Cancel",
		"failedToFetch": "Failed to search for addresses / areas"
	},
	"no": {
		"locationsFound": "{count} treff",
		"save": "Lagre",
		"cancelLabel": "Avbryt",
		"failedToFetch": "Kunne ikke søke etter adresser / områder"
	}
}
</i18n>
<template>
	<div>
		<t-multiselect-button
			:title="label"
			icon="geosearch"
			:limit="$options.SERVER_RETURN_LIMIT"
			:placeholder="placeholder"

			:searchValue="searchValue"
			:items="filteredItems"
			:selected="selected"

			:isInvalid="isInvalid"
			:validationMessage="validationMessage"

			@focus="onFocus"
			@input="onInput"
			@blur="onBlur"
			@change="onChange"
			@keyDownEnter="onEnter"
			@button:click="isDialogShown = true"
		>
			<template #optionItem="slotProps">
				<t-menu-icon-option-item
					:key="slotProps.item.id"
					:icon="getItemIcon(slotProps.item)"
					:value="slotProps.item"
					:selected="slotProps.isSelected"
					type="bp"
					class="location-format"
				>
					<div v-if="slotProps.item.postalCode" class="item">
						<div class="item-main-text">{{ slotProps.item.name }}</div>
						<div class="item-sub-text">{{ slotProps.item.street }}, {{ slotProps.item.postalCode }} {{ slotProps.item.postalPlace }}</div>
					</div>
					<div v-else class="item">{{ slotProps.item.name || slotProps.item.label}}</div>
				</t-menu-icon-option-item>
			</template>
		</t-multiselect-button>

		<LocationsDialog
			:isShown="isDialogShown"
			:icon="icon"
			:dialogType="dialogType"
			:existingFilters="selected"
			:searchText="searchValue"
			:title="dialogTitle"
			@selected="onDialogSelected"
			@close="isDialogShown = false"
		/>

	</div>
</template>

<script>
import { mapActions, mapState } from './store/locations.store.js';
import popoverMixin from '@/components/evergreen/inputs/popoverMixin';
import LocationsDialog from './LocationsDialog';
import TMenuIconOptionItem from '@/components/evergreen/menu/TMenuIconOptionItem';
import TMultiselectButton from '@/components/evergreen/timpex/inputs/TMultiselectButton';

export default {
	components: { TMultiselectButton, TMenuIconOptionItem, LocationsDialog },
	SERVER_RETURN_LIMIT: 50,
	mixins: [ popoverMixin ],
	props: {
		dialogType: {
			required: true,
			type: String,
			default: null
			// identifier
		},
		icon: String,
		component: Object,
		componentProps: Object,
		dialogTitle: String,
		selected: Array,
		disableGlobalAddress: Boolean,
		label: String,
		isInvalid: {
			type: Boolean,
			default: false,
		},
		validationMessage: String,
		placeholder: { type: String },
	},
	data() {
		return {
			items: [],
			isDialogShown: false,
			searchValue: '',
			lastSearch: null,
			searchTimeout: null,
			focused: false,
		};
	},
	computed: {
		...mapState(['locations', 'dialog', 'fromDialog']),
		
		filteredItems() {
			if (this.searchValue === '')
				return this.items;
			
			return this.items.filter( item => {
				
				if (!item)
					return false;
				
				let loweredSearchValue = this.searchValue.toLowerCase();
				
				let ret = true;
				for (let c of loweredSearchValue.split(' ')) {
					ret = ret &&
						( (item.postalPlace && item.postalPlace.toLowerCase().includes(c))
							|| ( item.postalCode && item.postalCode.toString().toLowerCase().includes(c) )
							|| ( item.street && item.street.toLowerCase().includes(c))
							|| ( item.name && item.name.toLowerCase().includes(c))
						);
				}
				return ret;
			});
		},
	},
	beforeDestroy() {
		if (this.map) {
			this.map.off();
			this.map.remove();
			this.map = null;
		}
	},
	beforeMount() {
		this.$emit('dialogType', this.fromDialog);
	},
	mounted() {
		if (this.dialog && this.fromDialog === this.dialogType ) {
			this.isDialogShown = true;
			this.setDialog(false);
		}
	},
	methods: {
		...mapActions(['fetchLocation', 'setDialog']),

		onFocus() {
			this.focused = true;
		},
		onInput(searchValue) {
			this.searchValue = searchValue;
			this.$emit('input', searchValue);

			if (this.shouldCancelNewSearch(searchValue, this.lastSearch, this.items))
				return;

			clearTimeout(this.searchTimeout);
			this.searchTimeout = setTimeout(() => {

				this.search(searchValue);

			}, 250);
		},
		shouldCancelNewSearch(searchValue, lastSearch, items) {
			return (
				searchValue === ''
				|| (
					items.length < this.$options.SERVER_RETURN_LIMIT
					&& searchValue.startsWith( lastSearch )
				)
			);
		},
		onChange(value) {
			this.$emit('select', value);
			this.searchValue = '';
			this.$emit('input', '');
		},
		async onEnter() {
			if (this.searchValue === '') {
				this.$emit('enter');
				return;
			}
			let oldSearchValue = this.searchValue;
			
			clearTimeout(this.searchTimeout);
			await this.search(this.searchValue);

			if (
				!this.items
				|| this.items.length !== 1
			) {
				this.$emit('select', oldSearchValue);
			}

			this.searchValue = '';
			this.$emit('input', '');
		},
		onSelected(value) {
			this.$emit('select', value);
			this.searchValue = '';
			this.$emit('input', '');
		},
		onBlur() {
			this.focused = false;
		},
		onDialogSelected(selections) {
			this.$emit('multiselect', selections);
			this.searchValue = '';
			this.$emit('input', '');
		},
		getItemIcon(item) {
			if (!item) return undefined;
			switch (item.type) {
			case 'postalCode': return 'map-marker';
			case 'polygon-filter': return 'polygon-filter';
			default: return 'home';
			}
		},
		async search(searchValue) {

			try {
				this.items = await this.fetchLocation({
					searchFilter: searchValue,
					disableGlobalAddress: this.disableGlobalAddress
				});

				if (
					!this.focused
					&& this.items
					&& this.items.length === 1
				) {
					this.$emit('select', this.items[0]);
					this.searchValue = '';
					this.$emit('input', '');
					
				} else if (!this.focused) {
					this.searchValue = '';
					this.$emit('input', '');
				}
			} catch (e) {
				this.$notify({
					type: 'error',
					title: this.$t('failedToFetch')
				});
			}
			this.lastSearch = searchValue;
		},
		clearInput() {
			this.searchValue = '';
			this.$emit('input', '');
		},
	}
};
</script>

<style lang="scss" scoped>
.location-format {
	text-transform: capitalize;
}
.item {
	display: flex;
	flex-direction: column;
	justify-content: center;
	height: 100%;
}

.item-main-text {
	height:16px;
	line-height: 16px;
	font-weight: 600;
	overflow: hidden;
	white-space: nowrap;
}

.item-sub-text {
	height:16px;
	font-size: 10px;
	font-weight: normal;
	font-style: normal;
	font-stretch: normal;
	line-height: 1.6;
	letter-spacing: normal;
	overflow: hidden;
	white-space: nowrap;
}
</style>
